import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { addBreadcrumb } from "../config/sentry";
// #591: the GMA-1275 safe accessors now live in ONE shared module so
// AuthContext, App and TermsPage use the same hardening (no second copy).
import { safeGet, safeClear, __test__ as safeStorageTest } from "../utils/safeStorage";

// GMA-1275: Mobile Safari (iOS 18.7) can return a null `localStorage` accessor
// during page teardown / cross-context storage events. Sentry issue
// COMPASSCARE-FRONTEND-1G — "TypeError: null is not an object (evaluating
// 'localStorage.getItem')" — was thrown from the storage event listener while
// /call was unmounting after a dual-session emergency call. The handler is
// wired through AuthContext, so the throw collapsed the React tree and left
// /call blank. The hardened helpers below short-circuit to safe defaults and
// emit a single auth breadcrumb the first time storage is unreachable.

// Storage events from other tabs may carry many keys; we only re-evaluate the
// JWT when the relevant slots change. `event.key === null` is dispatched when
// `storage.clear()` is called in a peer tab (e.g. logout) and must still
// trigger a re-read.
const STORAGE_KEYS_OF_INTEREST = new Set(["token", "auth_time"]);

// Issue #360: the backend (cortex-be shapeJwtUserObject / createJwtToken)
// signs `user.id` as a NUMBER (Sequelize integer PK — users.id is int4), and
// every consumer relies on number semantics: falsy-id gates
// (`currentUser?.id ? … : null` in ClientContext — the #339/#354 session
// gates) and strict `===` comparisons against numeric API ids (Layout,
// CallFilter, RefinedSearch, useFilteredCallHistory, …). A string id claim
// (e.g. "0", which is truthy) silently flips that gating. Normalize once at
// the decode boundary so no consumer ever sees a string id:
//   - numeric strings coerce via Number()
//   - non-numeric values become null (NaN never leaks into gates)
//   - null ids and claims without an id key pass through unchanged
export const normalizeJwtUser = (user) => {
  if (
    !user ||
    typeof user !== "object" ||
    !("id" in user) ||
    user.id === null ||
    user.id === undefined
  ) {
    return user;
  }
  const id = Number(user.id);
  return { ...user, id: Number.isNaN(id) ? null : id };
};

function getJWTValue(key, defaultValue) {
  const jwtToken = safeGet("token");
  if (!jwtToken) {return;}

  const now = moment();
  // GMA-1909: the 30-minute idle expiry used to run HERE too, synchronously
  // during render, clearing all of localStorage. That was a third deadline
  // beside AuthContext's poll and the countdown, and being the earliest it won:
  // on a reload after an idle timeout it deleted the countdown's own deadline
  // before the hook could mount, so the idle-logout breadcrumb and the "you
  // were signed out" notice never happened. Idle expiry is now owned solely by
  // useSessionCountdown, which seeds itself from `auth_time` for sessions that
  // predate it. The JWT `exp` check below stays — token validity is a different
  // question from user inactivity, and the two were conflated.

  // Removed localStorage.setItem("auth_time", now.unix()) here in a previous
  // fix — it caused an infinite re-render loop by re-firing storage events on
  // every render. AuthContext refreshes auth_time on user activity (throttled).

  let jwtData;
  try {
    jwtData = jwtDecode(jwtToken);
    const exp = jwtData["exp"];
    if (now.unix() > exp) {
      safeClear();
      return;
    }
  } catch (_err) {
    safeClear();
  }

  const value = jwtData ? jwtData[key] : defaultValue;
  // Issue #360: the "user" claim is normalized at this single decode boundary
  // so every `currentUser` consumer sees a number-or-null id.
  return key === "user" ? normalizeJwtUser(value) : value;
}

export const useJwtAsState = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    return getJWTValue(key, defaultValue);
  });

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event && event.key && !STORAGE_KEYS_OF_INTEREST.has(event.key)) {
        return;
      }
      try {
        setValue(getJWTValue(key, defaultValue));
      } catch (err) {
        try {
          addBreadcrumb("auth.useJwtAsState.handler_error", {
            category: "auth",
            level: "error",
            message: err && err.message ? err.message : String(err),
          });
        } catch (_inner) {
          // never throw out of a storage event listener.
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key, defaultValue]);

  return [value, setValue];
};

export const __test__ = {
  getJWTValue,
  resetStorageUnavailableBreadcrumbDedupe:
    safeStorageTest.resetStorageUnavailableBreadcrumbDedupe,
};
